import React from "react";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

import { DropdownValue } from ".";
import clsx from "clsx";
import Text from "../../../styleguide/Text";
import LoadingIndicator from "../LoadingIndicator";
import SvgIcon from "../../../helpers/SvgIcon";

const radioIcon = <SvgIcon type="RADIO_BUTTON_UNCHECKED" fill="currentColor" />;
const radioCheckedIcon = (
  <SvgIcon type="RADIO_BUTTON_CHECKED" fill="currentColor" />
);

export default function SingleDropdown({
  data,
  singleValue,
  setSingleValue,
  name,
  title = " ",
  className,
  style,
  placeholder,
  isLoading,
  disabled,
  withVerticalSpacer,
  touched,
  error,
  noBottomSpacing = false,
}: {
  data: DropdownValue[];
  singleValue?: DropdownValue | null;
  setSingleValue?: (value: DropdownValue | null) => void;
  name?: string;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  withVerticalSpacer?: boolean;
  touched?: boolean;
  error?: string;
  noBottomSpacing?: boolean;
}) {
  const handleOnSelect = (_: any, values: any) => {
    if (values && setSingleValue) {
      setSingleValue(values);
    }
  };
  return (
    <div
      className={`${noBottomSpacing ? "" : "mb-7"} w-full ${
        withVerticalSpacer ? "px-1" : ""
      } ${className}`}>
      {title && (
        <label
          htmlFor={`${name ?? title}-single-checkbox-label`}
          className="mb-2 block text-slate-900">
          <Text variant="body1" isBold>
            {title}
          </Text>
        </label>
      )}
      <Autocomplete
        id={`${name ?? title}-single-checkbox-label`}
        style={style}
        fullWidth
        disabled={disabled}
        options={data}
        value={singleValue}
        loading={isLoading}
        onChange={handleOnSelect}
        clearIcon={null}
        getOptionLabel={(option) => option.label.toString()}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.id}>
            <Checkbox
              icon={radioIcon}
              checkedIcon={radioCheckedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            className={clsx(
              !noBottomSpacing && "mb-3",
              "w-full rounded-control bg-surface-subtle [&_.MuiOutlinedInput-root]:h-[48px] [&_.MuiOutlinedInput-root]:rounded-control [&_.MuiOutlinedInput-root]:bg-surface-subtle",
            )}
            sx={{
              "& fieldset": { border: "none" },
            }}
            placeholder={placeholder}
            error={Boolean(error && touched)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? (
                    <LoadingIndicator color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
      {touched && error && (
        <div className="pt-3">
          <Text variant="body1" role="alert" className="text-danger">
            {error}
          </Text>
        </div>
      )}
    </div>
  );
}
